import { Component, createEffect, createSignal, on, onCleanup, Show } from 'solid-js'
import { userStore } from '../../../store/user'
import { characterStore } from '../../../store/character'
import { createDebounce, toDropdownItems } from '../../../shared/util'
import Select from '../../../shared/Select'
import { UI } from '/common/types'
import ColorPicker from '../../../shared/ColorPicker'
import Divider from '../../../shared/Divider'
import RangeInput from '../../../shared/RangeInput'
import { FormLabel } from '../../../shared/FormLabel'
import Sortable, { SortItem } from '../../../shared/Sortable'
import Message from '../../Chat/components/Message'
import { defaultUIsettings } from '/common/types/ui'
import { toInlineList } from './common'
import { Card } from '../../../shared/Card'
import { i18nStore } from '../../../store'

export const MessageUISettings: Component = (props) => {
  const i18n = i18nStore((s) => ({ t: s.t }))
  const state = userStore((s) => ({
    ui: s.ui,
    current: s.current,
    profile: s.profile,
    user: s.user,
  }))

  const chars = characterStore((s) => ({
    characters: s.characters,
    impersonating: s.impersonating,
  }))

  const [tryCustomUI, unsubCustomUi] = createDebounce((update: Partial<UI.CustomUI>) => {
    userStore.tryCustomUI(update)
  }, 50)

  onCleanup(() => unsubCustomUi())

  const [inline, setInline] = createSignal<SortItem[]>([])

  createEffect(
    on(
      () => state.ui.msgOptsInline,
      (opts) => {
        if (inline().length) return

        setInline(toInlineList(opts))
      }
    )
  )

  const updateInline = (items: SortItem[]) => {
    const next = { ...defaultUIsettings.msgOptsInline }
    for (let i = 0; i < items.length; i++) {
      const item = items[i]
      next[item.value as UI.MessageOption] = {
        outer: !!item.enabled,
        pos: i,
      }
    }

    userStore.saveUI({ msgOptsInline: next })
    setInline(toInlineList(next))
  }

  return (
    <>
      <Card>
        <FormLabel helperText={i18n.t.settings_ui_avatars} />
        <div class="flex flex-row justify-start gap-4">
          <Select
            fieldName="avatarSize"
            label={i18n.t.settings_ui_size}
            items={toDropdownItems(UI.AVATAR_SIZES)}
            value={state.ui.avatarSize}
            onChange={(item) => userStore.saveUI({ avatarSize: item.value as any })}
          />
          <Select
            fieldName="avatarCorners"
            label={i18n.t.settings_ui_cornerRadius}
            items={toDropdownItems(UI.AVATAR_CORNERS)}
            value={state.ui.avatarCorners}
            onChange={(item) => userStore.saveUI({ avatarCorners: item.value as any })}
          />
        </div>
        <div class="flex flex-row gap-4">
          <RangeInput
            value={state.ui.customAvatarWidth ?? 32}
            label={i18n.t.settings_ui_width}
            onChange={(item) => userStore.saveUI({ customAvatarWidth: item })}
            step={1}
            min={8}
            max={128}
            disabled={state.ui.avatarSize !== 'custom'}
          />

          <RangeInput
            value={state.ui.customAvatarHeight ?? 32}
            label={i18n.t.settings_ui_height}
            onChange={(item) => userStore.saveUI({ customAvatarHeight: item })}
            step={1}
            min={8}
            max={128}
            disabled={state.ui.avatarSize !== 'custom'}
          />
        </div>
      </Card>

      <ColorPicker
        label={i18n.t.settings_ui_messageBackgroundColor}
        fieldName="messageColor"
        helperText={
          <span class="link" onClick={() => userStore.saveCustomUI({ msgBackground: 'bg-800' })}>
            {i18n.t.settings_ui_resetToDefault}
          </span>
        }
        onInput={(color) => tryCustomUI({ msgBackground: color })}
        onChange={(color) => userStore.saveCustomUI({ msgBackground: color })}
        value={state.current.msgBackground}
      />
      <ColorPicker
        label={i18n.t.settings_ui_botMessageBackgroundColor}
        fieldName="botMessageColor"
        helperText={
          <>
            <span class="link" onClick={() => userStore.saveCustomUI({ botBackground: 'bg-800' })}>
              {i18n.t.settings_ui_resetToDefault}
            </span>
          </>
        }
        onInput={(color) => tryCustomUI({ botBackground: color })}
        onChange={(color) => userStore.saveCustomUI({ botBackground: color })}
        value={state.current.botBackground}
      />
      <ColorPicker
        label={i18n.t.settings_ui_chatTextColor}
        fieldName="chatTextColor"
        helperText={
          <span class="link" onClick={() => userStore.saveCustomUI({ chatTextColor: 'text-800' })}>
            {i18n.t.settings_ui_resetToDefault}
          </span>
        }
        onInput={(color) => tryCustomUI({ chatTextColor: color })}
        onChange={(color) => userStore.saveCustomUI({ chatTextColor: color })}
        value={state.current.chatTextColor}
      />
      <ColorPicker
        label={i18n.t.settings_ui_chatEmphasisColor}
        fieldName="chatEmphasisColor"
        helperText={
          <span
            class="link"
            onClick={() => userStore.saveCustomUI({ chatEmphasisColor: 'text-600' })}
          >
            {i18n.t.settings_ui_resetToDefault}
          </span>
        }
        onInput={(color) => tryCustomUI({ chatEmphasisColor: color })}
        onChange={(color) => userStore.saveCustomUI({ chatEmphasisColor: color })}
        value={state.current.chatEmphasisColor}
      />
      <ColorPicker
        label={i18n.t.settings_ui_chatQuoteColor}
        fieldName="chatQuoteColor"
        helperText={
          <span class="link" onClick={() => userStore.saveCustomUI({ chatQuoteColor: 'text-800' })}>
            {i18n.t.settings_ui_resetToDefault}
          </span>
        }
        onInput={(color) => tryCustomUI({ chatQuoteColor: color })}
        onChange={(color) => userStore.saveCustomUI({ chatQuoteColor: color })}
        value={state.current.chatQuoteColor || '--text-800'}
      />
      <ColorPicker
        label={i18n.t.settings_ui_chatQuoteEmphasisColor}
        fieldName="chatQuoteEmphasisColor"
        helperText={
          <span
            class="link"
            onClick={() => userStore.saveCustomUI({ chatQuoteEmphasisColor: 'text-800' })}
          >
            {i18n.t.settings_ui_resetToDefault}
          </span>
        }
        onInput={(color) => tryCustomUI({ chatQuoteEmphasisColor: color })}
        onChange={(color) => userStore.saveCustomUI({ chatQuoteEmphasisColor: color })}
        value={state.current.chatQuoteEmphasisColor || '--text-800'}
      />
      <Select
        fieldName="chatQuoteEmphasisWeight"
        label={i18n.t.settings_ui_chatQuoteEmphasisWeight}
        inline
        items={[
          { label: i18n.t.settings_ui_none, value: 'unset' },
          { label: i18n.t.settings_ui_bold, value: 'bold' },
        ]}
        onChange={(item) =>
          userStore.saveCustomUI({ chatQuoteEmphasisWeight: item.value as string })
        }
        value={state.current.chatQuoteEmphasisWeight || 'unset'}
      />
      <Select
        fieldName="chatWidth"
        label={i18n.t.settings_ui_contentWidth}
        inline
        items={[
          { label: i18n.t.settings_ui_narrow, value: 'narrow' },
          { label: i18n.t.settings_ui_large, value: 'full' },
          { label: i18n.t.settings_ui_xlarge, value: 'xl' },
          { label: i18n.t.settings_ui_2xlarge, value: '2xl' },
          { label: i18n.t.settings_ui_3xlarge, value: '3xl' },
          { label: i18n.t.settings_ui_100, value: 'fill' },
        ]}
        onChange={(item) => userStore.saveUI({ chatWidth: item.value as any })}
        value={state.ui.chatWidth}
      />
      <RangeInput
        fieldName="msgOpacity"
        value={state.ui.msgOpacity}
        step={0.05}
        label={i18n.t.settings_ui_messageOpacity}
        helperText={i18n.t.settings_ui_messageOpacityHelper}
        min={0}
        max={1}
        onChange={(value) => userStore.saveUI({ msgOpacity: value })}
      />
      <Divider />
      <FormLabel
        label={i18n.t.settings_ui_inlineMessageOptions}
        helperText={i18n.t.settings_ui_inlineMessageOptionsHelper}
      ></FormLabel>
      <Show when={inline().length > 0}>
        <Sortable items={inline()} onChange={updateInline} />
      </Show>
      <Divider />
      <div class="text-lg font-bold">{i18n.t.settings_ui_preview}</div>
      <Show when={chars.characters.list.length > 0}>
        <div class="bg-600 flex w-full flex-col gap-2 rounded-md p-2">
          <Message
            index={-1}
            editing={false}
            messageId={'example-msg-1'}
            content={'*I wave excitedly* Hello world!\nHow are you today?'}
            characterId={chars.characters.list[0]?._id}
            onRemove={noop}
            sendMessage={() => {}}
            isPaneOpen={false}
            preset={undefined}
          />

          <Show when={state.profile}>
            <Message
              index={-1}
              editing={false}
              messageId={'example-msg-2'}
              content='*I wave back* Hi {{char}}!\nFancy meeting you here! I heard someone say "The weather is great today!"\n"How about we have some *fun* today?"'
              characterId={chars.impersonating?._id}
              userId={state.user?._id}
              onRemove={noop}
              sendMessage={() => {}}
              isPaneOpen={false}
              preset={undefined}
            />
          </Show>
        </div>
      </Show>
    </>
  )
}

function noop() {}
